import React, { useEffect, useState } from 'react';
import { PageTitle } from 'styles/Global/commonStyles';
import moment from 'moment';
import useToggle from 'hooks/CustomHooks/useToggle';
import { useLoginStore } from 'store/useLoginDataStore';
import { useToastStore } from 'store/useToastStore';
import { HospitalPageLabel, FilterWrapper, DiagnosisInnerLayout, DiagnosisLayout, DiagnosisTapsContainer, DiagnosisItemButton } from 'styles/Global/hospitalStyles';
import DiagnosisPaginationSearchFilter from '../../../../functions/diagnosisPaginationSearchFilter';
import { diagnosisPaginationSorting } from 'functions/paginationSorting';
import usePaginationStateHooks from 'hooks/CustomHooks/usePaginationStateHooks';
import { useLoadingStore } from 'store/useLoadingStore';
import _ from 'lodash';
import { DiagnosisStatus } from 'utils/statusCode';
import MindUpAdminDiagnosisListPagination from '../MindUpAdminDiagnosisListPage/MindUpAdminDiagnosisListPagination';
import MindUpAdminAllReservationConsultationFilter from './MindUpAdminAllReservationConsultationFilter';
import { RoundBadgeSelectS } from 'components/Common/Badge';
import { MindUpAdminAllReservationConsultationPaginationrMenu } from './constants';
import BasicConfirmModal from 'components/Modal/BasicConfirmModal';
import styled from 'styled-components';
import { Body3Medium } from 'styles/Global/typography';
import { BorderColor, IconColor } from 'styles/Global/colorSemantic';
import { useDiagnosisListByStatusQuery } from 'api/Queries/Diagnosis/useDiagnosisListByStatusQuery';
import { IDDiagnosis } from 'models/DCK/basicInterface';
import IcCalendar from 'assets/Icons/Calendar/IcCalendar';
import { AssignCounselorRqData } from 'models/MindUp/MindUpTypes';
import { DCKDiagnosisApi } from 'api/DCK/DCKDiagnosisApi';

export type ReservationType = {
    diagnosisId: string;
    reservationDate: string;
};

const MindUpAdminAllReservationConsultationPage = () => {
    const { setToast } = useToastStore();
    const { loginData } = useLoginStore();
    const { setLoading } = useLoadingStore();

    const [initData, setInitData] = useState<IDDiagnosis[]>();
    const [filterData, setFilterData] = useState<IDDiagnosis[] | undefined>(initData);
    const [selectedSearchFilter, setselectedSearchFilter] = useState<number>(0);
    const [searchValue, setSearchValue] = useState<string>('');
    const [sortField, setSortField] = useState<string>('');
    const [sortDirections, setSortDirections] = useState<Record<string, boolean>>({ patientName: false, patientIdentityNumber: false, diagnosisDateTime: false });
    const [paginationMenu, setPaginationMenu] = useState<number>(0);
    const [devideDiagnosisProcess, setDevideDiagnosisProcess] = useState({ ALL: 0, BEFORE_REQUEST: 0, WAITING: 0 });
    const [isShowReservationAlert, setIsShowReservationAlert] = useState<boolean>(false);
    const [counselData, setCounselData] = useState<ReservationType>();

    const [onSearchFilterDropdown, setOnSearchFilterDropdown] = useToggle();

    const { totalPages, setPage, currentPage, pageGroup, handlePageGroup, currentData } = usePaginationStateHooks(filterData);

    // const { data: diagnosisListData, isLoading: diagnosisListLoading, refetch: refetchDiagnosisListData } = useDiagnosisListQuery(paginationMenu);
    const { data: diagnosisListData, isLoading: diagnosisListLoading, refetch: refetchDiagnosisListData } = useDiagnosisListByStatusQuery({ status: [3] }, paginationMenu);

    // Data Fetching
    useEffect(() => {
        processDiagnosisData();
        handleResetButton();
    }, [diagnosisListData]);

    // 데이터 처리 함수
    const processDiagnosisData = () => {
        if (!diagnosisListData) return;
        // const isShowCounselorList = (counselItem: Omit<AddDiagnosisApiResponseData, 'prescriptionMemo' | 'assessmentAge'>) => counselItem.status === DiagnosisStatus.completed;
        const beforeRequestList = (counselItem: IDDiagnosis) => counselItem.counselDateTimeList.length === 0;
        const waitingList = (counselItem: IDDiagnosis) => counselItem.counselDateTimeList.length > 0;
        let paginationMenuCount = { ALL: 0, BEFORE_REQUEST: 0, WAITING: 0 };

        // initData 설정을 위한 sorting
        // const processedData = data.sort((a, b) => b.diagnosisDateTime.localeCompare(a.diagnosisDateTime)).reverse();
        paginationMenuCount.ALL = diagnosisListData?.length || 0;
        diagnosisListData?.filter((item) => (item.counselDateTimeList.length === 0 ? paginationMenuCount.BEFORE_REQUEST++ : paginationMenuCount.WAITING++));

        // pagination menu에 따라 filtering
        const sortedData =
            paginationMenu !== 0
                ? diagnosisListData
                      .filter(paginationMenu === 1 ? beforeRequestList : waitingList)
                      .sort((a, b) => b.diagnosisDateTime.localeCompare(a.diagnosisDateTime))
                      .reverse()
                : diagnosisListData.sort((a, b) => b.diagnosisDateTime.localeCompare(a.diagnosisDateTime)).reverse();

        setSortField('diagnosisDateTime');
        setSortDirections((prev) => ({
            ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
            diagnosisDateTime: true,
        }));
        setDevideDiagnosisProcess(paginationMenuCount);
        setInitData(sortedData);
        setFilterData(sortedData);
    };

    // handlePeriodFilter Dropdown
    const handleDropdown = (e: React.MouseEvent, idx: number, type: string) => {
        e.stopPropagation();
        setOnSearchFilterDropdown();
        setselectedSearchFilter(idx);
        setSearchValue('');
        setPage(1);
    };

    // SearchFilter function
    const handleSearchFilter = () => {
        DiagnosisPaginationSearchFilter(searchValue, setSortDirections, setSortField, selectedSearchFilter, initData, setPage, handlePageGroup, setFilterData, 'mindUpAdmin');
    };

    // reset Button function
    const handleResetButton = () => {
        setSearchValue('');
        setPage(1);
        handlePageGroup(0);
        setSortDirections((prev) => ({
            ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
            diagnosisDateTime: true,
        }));
        setSortField('diagnosisDateTime');
        setCounselData({ diagnosisId: '', reservationDate: '' });
        setselectedSearchFilter(0);
        // refetchDiagnosisListData();
    };

    // sorting function
    const handleSorting = (type: string) => {
        const direction = !sortDirections[type];

        setSortField(type);
        setSortDirections((prev) => ({
            ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
            [type]: direction,
        }));
        setFilterData(diagnosisPaginationSorting(type, filterData || [], direction));
    };

    const handleReservationConfirm = () => {
        setIsShowReservationAlert(true);
    };

    const formatDateString = (dateString: string): string => {
        // yyyymmddhhmm 형식 문자열에서 각 부분 추출
        const year = parseInt(dateString.slice(0, 4), 10);
        const month = parseInt(dateString.slice(4, 6), 10) - 1; // 월은 0부터 시작하므로 -1
        const day = parseInt(dateString.slice(6, 8), 10);
        const hours = parseInt(dateString.slice(8, 10), 10);
        const minutes = parseInt(dateString.slice(10, 12), 10);

        // Date 객체 생성
        const date = new Date(year, month, day, hours, minutes);

        // 요일을 배열로 정의
        const daysOfWeek = ['일', '월', '화', '수', '목', '금', '토'];
        const dayOfWeek = daysOfWeek[date.getDay()];

        // 원하는 형식으로 문자열 반환
        const amPm = hours < 12 ? '오전' : '오후';
        const formattedHours = hours % 12 === 0 ? 12 : hours % 12; // 12시간 형식으로 변환

        // 원하는 형식으로 문자열 반환
        return `${year}년 ${String(month + 1).padStart(2, '0')}월 ${String(day).padStart(2, '0')}일 (${dayOfWeek}) ${amPm} ${formattedHours}:${String(minutes).padStart(2, '0')}`;
    };

    // Reservation Alert Modal Leave Button
    const reservationModalConfirm = async () => {
        const rqData: AssignCounselorRqData = {
            diagnosisId: counselData?.diagnosisId ?? '',
            userId: loginData.id,
            counselDateTime: counselData?.reservationDate ?? '',
        };

        const changeStatusRqData = {
            _id: counselData?.diagnosisId ?? '',
            status: DiagnosisStatus.counselReserved,
        };
        setLoading(true);
        await DCKDiagnosisApi.updateAssignCounselor(rqData, loginData.token);
        const response = await DCKDiagnosisApi.changeDiagnosisStatus(changeStatusRqData, loginData.token);

        if (response.status === 'ok') {
            await refetchDiagnosisListData();
            setToast('상담 확정이 완료되었습니다.');
        } else {
            setLoading(false);
            console.log('stats 변경 실패');
        }

        setIsShowReservationAlert(false);
        setCounselData({ diagnosisId: '', reservationDate: '' });
    };

    // Reservation Alert Modal Info Content
    const ReservationInfoContent = () => {
        const parseDateTime = moment(counselData?.reservationDate, 'YYYYMMDDHHmm').format('YYYY/MM/DD A hh:mm');

        return (
            <InfoContainer>
                <IcCalendar color={IconColor.color_icon_normal} />
                <span>{parseDateTime}</span>
            </InfoContainer>
        );
    };

    return (
        <DiagnosisLayout>
            {isShowReservationAlert && (
                <BasicConfirmModal
                    title='해당 일시로 상담을 확정할까요?'
                    content='상담 일시를 확정하면 더 이상 수정이 불가능합니다.'
                    // content={`${formatDateString(counselData?.reservationDate ?? '')}으로 예약하시겠습니까?`}
                    firstButton='취소'
                    secondButton='상담 확정'
                    secondButtonType='fill'
                    cancelButton={() => {
                        setIsShowReservationAlert(false);
                    }}
                    confirmButton={reservationModalConfirm}
                    infoContent={<ReservationInfoContent />}
                />
            )}
            {!currentData || diagnosisListLoading ? null : (
                <DiagnosisInnerLayout>
                    <HospitalPageLabel onClick={() => window.location.reload()}>예약 대기 상담</HospitalPageLabel>
                    <PageTitle $marginbottom='24px'>예약 대기 상담</PageTitle>
                    {/* <RowDivider $backgroundcolor={BorderColor.color_border_neutral} $marginbottom='16px' /> */}
                    <DiagnosisTapsContainer $bottomMargin={'16px'}>
                        {MindUpAdminAllReservationConsultationPaginationrMenu.map((item, idx) => {
                            return (
                                <DiagnosisItemButton key={idx} onClick={() => setPaginationMenu(idx)} $idx={idx} $selected={paginationMenu === idx ? paginationMenu : undefined}>
                                    {item}
                                    <RoundBadgeSelectS $selected={paginationMenu === idx}>
                                        {idx === 0 ? devideDiagnosisProcess.ALL : idx === 1 ? devideDiagnosisProcess.BEFORE_REQUEST : devideDiagnosisProcess.WAITING}
                                    </RoundBadgeSelectS>
                                </DiagnosisItemButton>
                            );
                        })}
                    </DiagnosisTapsContainer>
                    <FilterWrapper>
                        <MindUpAdminAllReservationConsultationFilter
                            diagnosisNumber={filterData?.length}
                            selectedSearchFilter={selectedSearchFilter}
                            handleDropdown={handleDropdown}
                            setOnSearchFilterDropdown={setOnSearchFilterDropdown}
                            onSearchFilterDropdown={onSearchFilterDropdown}
                            onChange={setSearchValue}
                            searchValue={searchValue}
                            handleSearchFilter={handleSearchFilter}
                        />
                    </FilterWrapper>
                    <MindUpAdminDiagnosisListPagination
                        initNumber={initData?.length}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        pageGroup={pageGroup}
                        counselData={counselData}
                        handlePageGroup={handlePageGroup}
                        handlePageChange={setPage}
                        currentData={currentData}
                        handleSorting={handleSorting}
                        sortField={sortField}
                        sortDirections={sortDirections}
                        handleResetButton={handleResetButton}
                        handleReservationAlert={handleReservationConfirm}
                        handleCounselData={setCounselData}
                    />
                </DiagnosisInnerLayout>
            )}
        </DiagnosisLayout>
    );
};

export default MindUpAdminAllReservationConsultationPage;

const InfoContainer = styled(Body3Medium)`
    display: flex;
    align-items: center;
    width: 100%;
    margin-top: 4px;
    height: 60px;
    padding: 12px 20px;
    border: 1px solid ${BorderColor.color_border_neutral};
    border-radius: 4px;
    gap: 6px;
`;
